import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Card,
  Table,
  Button,
  Pagination,
  Select,
  Spinner,
  Alert,
} from "flowbite-react";
import { HiDocumentText, HiOutlineCalendar } from "react-icons/hi";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";

// --- Component ---
const ViewReceipts = () => {
  const store = useContext(Store);
  let [baseUrl] = store.url;
  let [, setInvoice] = store.invoice;
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentYear, setCurrentYear] = useState(""); // Empty string for no filter
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    limit: 20,
  });
  const navigate = useNavigate();

  // Helper to generate years for the filter dropdown (e.g., last 5 years)
  const currentMaxYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentMaxYear - i).map(
    String
  );

  // Fetch Receipts Function
  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError(null);
    let token = localStorage.getItem("royal-token");

    try {
      // Build the query string
      const url = new URL(`${baseUrl}/receipts`);
      url.searchParams.append("page", currentPage);
      url.searchParams.append("limit", pagination.limit); // Use current limit

      if (currentYear) {
        url.searchParams.append("year", currentYear);
      }

      const response = await apiRequest(url.toString(), "GET", null, token);

      setReceipts(response.data.receipts);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination.currentPage); // Sync page number from API
    } catch (err) {
      console.error("Failed to fetch receipts:", err);
      setReceipts([]);
      setPagination({ totalCount: 0, totalPages: 1, limit: 20 });
      setError(err.message || "Could not fetch receipts.");
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentYear, pagination.limit, baseUrl]);

  // Effect hook to run API call when page or filter changes
  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Handle pagination change
  const onPageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  // Handle year filter change
  const handleYearChange = (e) => {
    setCurrentYear(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const calculateTotalAmount = (rows) => {
    if (!rows || !Array.isArray(rows)) return 0;

    // Ensure 'amount' is treated as a number during summation
    return rows.reduce((total, row) => total + (Number(row.amount) || 0), 0);
  };

  return (
    <div>
      <title>View Receipts</title>
      <DashboardLayout active="viewReceipt" contentClassName="lg:p-8">
            <div className="bg-white min-h-[70vh]">
              <h1 className="text-3xl font-extrabold mb-6 text-gray-900 dark:text-white flex items-center">
                <HiDocumentText className="me-3" /> All Client Receipts
              </h1>

              {/* --- Filter & Controls --- */}
              <div className="flex justify-between items-center mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm">
                <div className="flex items-center space-x-4">
                  <HiOutlineCalendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <Select
                    id="yearFilter"
                    value={currentYear}
                    onChange={handleYearChange}
                    disabled={loading}
                  >
                    <option value="">Filter by Year (All)</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300 font-bold">
                  Total Receipts: {pagination.totalCount}
                </div>
              </div>

              <hr className="my-4" />

              {/* --- Loading & Error Feedback --- */}
              {loading && (
                <div className="flex justify-center p-8">
                  <Spinner size="xl" />
                  <span className="ml-2 text-lg">Fetching receipts...</span>
                </div>
              )}

              {error && (
                <Alert
                  color="failure"
                  onDismiss={() => setError(null)}
                  className="mb-4"
                >
                  <span className="font-medium">Error!</span> {error}
                </Alert>
              )}

              {/* --- Receipts Table --- */}
              {!loading && receipts.length > 0 && (
                <Card className="shadow-lg">
                  <div className="overflow-x-auto">
                    <Table hoverable>
                      <Table.Head>
                        <Table.HeadCell>Receipt No.</Table.HeadCell>
                        <Table.HeadCell>Client Name</Table.HeadCell>
                        <Table.HeadCell>Project</Table.HeadCell>
                        <Table.HeadCell>Date</Table.HeadCell>
                        <Table.HeadCell>Total Amount</Table.HeadCell>
                        <Table.HeadCell>Generated By</Table.HeadCell>
                        <Table.HeadCell>
                          <span className="sr-only">View</span>
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {receipts.map((receipt) => (
                          <Table.Row
                            key={receipt._id}
                            className="bg-white dark:border-gray-700 dark:bg-gray-800"
                          >
                            <Table.Cell className="font-medium text-gray-900 dark:text-white">
                              {receipt.receiptNumber}
                            </Table.Cell>
                            <Table.Cell>{receipt.invoiceName}</Table.Cell>
                            <Table.Cell>{receipt.project}</Table.Cell>
                            <Table.Cell>
                              {new Date(receipt.date).toLocaleDateString(
                                "en-US"
                              )}
                            </Table.Cell>
                            <Table.Cell className="font-bold text-green-600 dark:text-green-400">
                              &#8358;
                              {calculateTotalAmount(
                                receipt.rows
                              ).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })}
                            </Table.Cell>
                            <Table.Cell>
                              {`${receipt.generatedBy?.firstName} ${receipt.generatedBy?.lastName}` ||
                                "N/A"}
                            </Table.Cell>
                            <Table.Cell>
                              <Button
                                size="sm"
                                color="light"
                                onClick={() => {
                                  setInvoice(receipt);
                                  navigate("/preview-receipt");
                                  /* Implement View/Download Logic here */
                                }}
                              >
                                View
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </Card>
              )}

              {/* --- No Data Found --- */}
              {!loading && receipts.length === 0 && !error && (
                <Alert color="info" className="mt-6">
                  No receipts found for the selected filter or page.
                </Alert>
              )}

              {/* --- Pagination Controls --- */}
              <div className="flex justify-center my-6">
                {pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={onPageChange}
                    showIcons
                    previousLabel="Prev"
                    nextLabel="Next"
                    className="dark:bg-gray-900"
                  />
                )}
              </div>
            </div>
      </DashboardLayout>
    </div>
  );
};

export default ViewReceipts;
